import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { useKey, usePrevious } from 'react-use';
import styled from 'styled-components';

import {
  StoryState as APIStoryState,
  DivisionChange,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { isType } from '@sorare/core/src/gql';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import useBodyLock from '@sorare/core/src/hooks/useBodyLock';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { theme } from '@sorare/core/src/style/theme';

import useMarkStoriesAsRead from 'hooks/stories/useMarkStoriesAsRead';

import { Intro } from './Intro';
import { Promotion } from './Promotion';
import { Relegation } from './Relegation';
import {
  LastFixtureStoryQuery,
  LastFixtureStoryQueryVariables,
} from './__generated__/index.graphql';
import { StoryState } from './types';

const LAST_FIXTURE_STORY_QUERY = gql`
  query LastFixtureStoryQuery($sport: Sport!) {
    so5 {
      myAvailableStories(sport: $sport) {
        id
        aasmState
        readable {
          ... on FootballRivalsChallenge {
            id
          }
          ... on So5Fixture {
            slug
            sport
            mySo5Rankings {
              id
              eligibleDivisionChange
            }
            ...Intro_so5Fixture
            ...Promotion_so5Fixture
            ...Relegation_so5Fixture
          }
        }
      }
    }
  }
  ${Intro.fragments.so5Fixture}
  ${Promotion.fragments.so5Fixture}
  ${Relegation.fragments.so5Fixture}
` as TypedDocumentNode<LastFixtureStoryQuery, LastFixtureStoryQueryVariables>;

const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  position: fixed;
  inset: 0;
  /** we expect dialogs like claim rewards one to be displayed on top */
  z-index: ${theme.zIndex.modal - 1};
  overflow: hidden;
  background-color: var(--c-black);
`;

const LastFixtureStoryComponent = ({
  so5Fixture,
  onClose,
  storyId,
}: {
  so5Fixture: LastFixtureStoryQuery['so5']['myAvailableStories'][0]['readable'];
  onClose?: () => void;
  storyId: string;
}) => {
  const [open, setOpen] = useState(true);
  const { readStories: readFixtureStories } = useMarkStoriesAsRead();

  useBodyLock(open);

  const finalOnClose = () => {
    setOpen(false);
    onClose?.();
    readFixtureStories({
      storyIds: [storyId],
    });
  };
  useKey('Escape', finalOnClose);

  const [storyState, setStoryState] = useState<StoryState>('intro');

  const prevStoryState = usePrevious(storyState);

  if (!open || !isType(so5Fixture, 'So5Fixture')) {
    return null;
  }
  const hasPromotion = so5Fixture.mySo5Rankings?.some(
    ranking => ranking.eligibleDivisionChange === DivisionChange.PROMOTED
  );
  const hasRelegation = so5Fixture.mySo5Rankings?.some(
    ranking => ranking.eligibleDivisionChange === DivisionChange.RELEGATED
  );

  return (
    <Portal id="full-screen-animation">
      <Wrapper>
        {storyState === 'intro' && (
          <Intro
            onClose={finalOnClose}
            so5Fixture={so5Fixture}
            onNext={
              hasPromotion || hasRelegation
                ? () => {
                    if (hasPromotion) {
                      setStoryState('promotion');
                    } else {
                      setStoryState('relegation');
                    }
                  }
                : undefined
            }
          />
        )}
        {storyState === 'promotion' && (
          <Promotion
            so5Fixture={so5Fixture}
            onNext={
              hasRelegation
                ? () => {
                    setStoryState('relegation');
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'relegation' && (
          <Relegation
            from={prevStoryState}
            so5Fixture={so5Fixture}
            onClose={finalOnClose}
          />
        )}
      </Wrapper>
    </Portal>
  );
};

type Props = {
  sport: Sport;
};
export const LastFixtureStory = ({ sport }: Props) => {
  const {
    flags: { useRewardsFlowNewDesign = false },
  } = useFeatureFlags();
  const { data } = useQuery(LAST_FIXTURE_STORY_QUERY, {
    variables: {
      sport,
    },
    skip: useRewardsFlowNewDesign || sport !== Sport.FOOTBALL,
  });
  // we only care in the frontend for the first story for now
  const story = data?.so5?.myAvailableStories?.[0];
  const so5Fixture = story?.readable;
  if (!so5Fixture || !isType(so5Fixture, 'So5Fixture')) {
    return null;
  }

  return (
    story.aasmState === APIStoryState.READY && (
      <LastFixtureStoryComponent so5Fixture={so5Fixture} storyId={story.id} />
    )
  );
};
